/**
 * State panels view — Faz 8.1.
 *
 * Snapshot of the kernel reducer state at a specific tickId.
 * Five collapsible panels (one per slice) show the
 * `[key, value]` tuples; the per-slice view collapses by default
 * when empty so the operator focuses on what's live.
 */

import { useEffect, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client.js";
import { fetchState, queryKeys } from "../api/queries.js";
import { JsonTree } from "../components/JsonTree.js";
import type { ReplayStateViewWire } from "../api/types.js";

const SLICES: ReadonlyArray<{ key: keyof ReplayStateViewWire; label: string }> = [
  { key: "freezeStates", label: "Freeze States" },
  { key: "positionStates", label: "Position States" },
  { key: "reduceOnlyStates", label: "Reduce-Only States" },
  { key: "openOrdersStates", label: "Open Orders" },
  { key: "realizedPnl", label: "Realized PnL Window" },
];

export const StatePanelsView = (): ReactElement => {
  const params = useParams<{ tickId: string }>();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string>(params.tickId ?? "");

  useEffect(() => {
    setPending(params.tickId ?? "");
  }, [params.tickId]);

  const tickId = params.tickId ?? "";
  const query = useQuery({
    queryKey: queryKeys.state(tickId),
    queryFn: () => fetchState(tickId),
    enabled: tickId !== "",
  });

  const onJump = (e: React.FormEvent): void => {
    e.preventDefault();
    if (pending !== "") navigate(`/state/${encodeURIComponent(pending)}`);
  };

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end gap-3">
        <h2 className="text-lg font-semibold">State Snapshot</h2>
        <form onSubmit={onJump} className="flex items-end gap-2">
          <label className="flex flex-col text-xs text-slate-400">
            tickId
            <input
              value={pending}
              onChange={(e) => setPending(e.target.value)}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm font-mono"
              placeholder="e.g. 1234"
            />
          </label>
          <button
            type="submit"
            className="rounded bg-sky-600 px-3 py-1 text-sm font-semibold text-white hover:bg-sky-500"
          >
            Jump
          </button>
        </form>
      </header>

      {tickId === "" && (
        <p className="text-sm text-slate-400">
          Enter a tickId to render kernel state at that point in the timeline.
        </p>
      )}

      {query.isLoading && <p data-testid="loading">Loading…</p>}
      {query.isError && (
        <p className="text-rose-400" data-testid="error">
          {query.error instanceof ApiError
            ? `${query.error.server.kind}: ${query.error.server.message}`
            : "Unknown error"}
        </p>
      )}

      {query.data && (
        <>
          <div className="rounded border border-slate-700 bg-slate-900/40 p-3 text-sm">
            <div>
              tickId: <span className="font-mono">{query.data.tickId}</span> · kind:{" "}
              <span className="font-mono">{query.data.eventKind}</span>
            </div>
            <div className="text-xs text-slate-400">digest: {query.data.digest}</div>
          </div>

          <details data-testid="digest-manifest" className="rounded border border-slate-700 bg-slate-900/30">
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold">
              Decision-state digest slices ({query.data.digestManifest.slices.length})
            </summary>
            <div className="p-3">
              <JsonTree value={query.data.digestManifest} />
            </div>
          </details>

          {SLICES.map(({ key, label }) => {
            const slice = query.data!.stateView[key];
            const empty = slice.length === 0;
            return (
              <details
                key={key}
                open={!empty}
                data-testid={`panel-${String(key)}`}
                className="rounded border border-slate-700 bg-slate-900/30"
              >
                <summary className="cursor-pointer border-b border-slate-700 px-3 py-2 text-sm font-semibold">
                  {label} ({slice.length})
                </summary>
                <div className="p-3">
                  {empty ? (
                    <p className="text-xs text-slate-500">empty</p>
                  ) : (
                    <ul className="space-y-2">
                      {slice.map(([k, v]) => (
                        <li key={k} className="space-y-1">
                          <div className="font-mono text-xs text-slate-400">{k}</div>
                          <JsonTree value={v} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </details>
            );
          })}
        </>
      )}
    </section>
  );
};
